import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { login, socialLogin, fetchUser } from '../../store/slices/authSlice';
import { Form, Button, Container, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import {
    FaHeart,
    FaEnvelope,
    FaLock,
    FaSpinner,
    FaEye,
    FaEyeSlash,
    FaCheckCircle,
    FaShieldAlt,
    FaRocket,
    FaUsers,
    FaChartLine,
    FaClock,
    FaBolt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import LandingNavbar from '../../components/layout/LandingNavbar';
import LandingFooter from '../../components/layout/LandingFooter';
import './Auth.css';


const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(login(formData)).unwrap();

      // Fetch updated user data to check profile completion
      await dispatch(fetchUser());

      toast.success('✅ Connexion réussie ! Bienvenue sur CHATOO 💕');

      // Check if profile is complete and redirect accordingly
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.profile && user.profile.is_complete) {
        navigate('/home');
      } else {
        navigate('/complete-profile');
      }

    } catch (err) {
      const msg = err?.message || err?.detail || (typeof err === 'string' ? err : 'Identifiants incorrects. Vérifiez votre email/nom d\'utilisateur et mot de passe.');
      console.error('Login failed:', msg, err);
      // Rediriger vers la page de vérification uniquement si le serveur demande
      // explicitement de vérifier le compte (403 "Please verify your email or phone")
      const needsVerify = msg.toLowerCase().includes('please verify') ||
                          msg.toLowerCase().includes('verify your email') ||
                          msg.toLowerCase().includes('verify your phone');
      if (needsVerify) {
        navigate('/verify?identifier=' + formData.identifier);
      } else {
        toast.error('\u274c ' + msg);
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const result = await dispatch(socialLogin({ provider: 'google', token: credentialResponse.credential })).unwrap();
      const user = result.user;

      if (user && user.profile && user.profile.is_complete) {
        toast.success('\u2705 Connexion Google r\u00e9ussie ! Bienvenue sur CHATOO \ud83d\udc95');
        navigate('/home');
      } else {
        toast.success('\u2705 Connexion Google r\u00e9ussie ! Compl\u00e9tez votre profil pour continuer \ud83d\udc95');
        navigate('/complete-profile');
      }
    } catch (err) {
      console.error('Google login failed:', err);
      toast.error('❌ Connexion Google échouée : ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleGoogleError = () => {
    toast.error('❌ Connexion Google annulée ou échouée');
  };

  return (
    <div className="auth-page-wrapper">
      <LandingNavbar />
      <div className="auth-page">
        <Container>

        <Row className="justify-content-center py-5">
          <Col lg={5} xl={6}>
            <Card className="auth-card shadow-lg">
              <Row className="g-0">
                <Col md={12} className="auth-form-section">

                  <div className="auth-form-content">
                    <h3 className="auth-form-title">Bienvenue de retour 💕</h3>
                    <p className="auth-form-subtitle">
                      Connectez-vous pour continuer votre aventure amoureuse
                    </p>

                    {/* Google Sign-In */}
                    <div className="auth-social-buttons mb-3">
                      <div className="google-btn-wrapper">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          size="large"
                          theme="outline"
                          shape="rectangular"
                          logo_alignment="left"
                          text="signin_with"
                          locale="fr"
                          width="400"
                        />
                      </div>
                    </div>

                    <div className="auth-divider">
                      <span>OU</span>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <Alert variant="danger" className="auth-alert">
                        <strong>Erreur :</strong> {error.message || error.detail || 'Une erreur est survenue'}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label-custom">
                          Email ou nom d'utilisateur
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="input-icon-group">
                            <FaEnvelope />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            name="identifier"
                            value={formData.identifier}
                            onChange={(e) => setFormData({...formData, identifier: e.target.value})}
                            required
                            className="form-control-custom"
                            placeholder="votre@email.com ou username"
                            autoComplete="off"
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="form-label-custom">
                          Mot de passe
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="input-icon-group">
                            <FaLock />
                          </InputGroup.Text>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className="form-control-custom"
                            placeholder="Votre mot de passe"
                            autoComplete="new-password"
                          />
                          <InputGroup.Text
                            className="input-icon-group password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: 'pointer' }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>

                      <div className="form-options mb-4">
                        <Form.Check
                          type="checkbox"
                          label="Se souvenir de moi"
                          className="remember-me"
                        />
                        <Link to="/forgot-password" className="forgot-password">
                          Mot de passe oublié ?
                        </Link>
                      </div>

                      <Button
                        type="submit"
                        className="btn-auth-submit w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Connexion en cours...
                          </>
                        ) : (
                          <>
                            <FaBolt className="me-2" />
                            Retrouver mes Loves 💕
                          </>
                        )}
                      </Button>
                    </Form>

                    <div className="auth-footer">
                      <p>
                        Nouveau sur CHATOO ?{' '}
                        <Link to="/register" className="auth-link">
                          Créer mon profil LOVE 💕
                        </Link>
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>

        {/* Background Decoration */}
        <div className="auth-background">
          <div className="floating-heart heart-1">❤️</div>
          <div className="floating-heart heart-2">💕</div>
          <div className="floating-heart heart-3">💖</div>
          <div className="floating-heart heart-4">💗</div>
          <div className="floating-heart heart-5">💓</div>
          <div className="floating-heart heart-6">💘</div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );

};

export default Login;
